import React, { useState, useEffect } from 'react';
import { T } from '../theme';
import { getRiskConfig, saveRiskConfig, resetRiskConfig, DEFAULT_RISK_CONFIG } from '../riskConfig';
import { Save, RotateCcw, AlertTriangle, CheckCircle, ChevronDown, ChevronRight } from 'lucide-react';

export default function RiskSettings({ onConfigChange }) {
  const [config, setConfig] = useState(() => getRiskConfig());
  const [expanded, setExpanded] = useState({ revenue: true, position: false, compliance: false, strategy: false });
  const [saved, setSaved] = useState(false);

  const catKeys = ["revenue", "position", "compliance", "strategy"];
  const totalMax = 100;

  // Validation
  const catSum = catKeys.reduce((s, k) => s + config.categories[k].maxPts, 0);
  const catValid = catSum === totalMax;

  const factorSums = {};
  const factorValid = {};
  for (const k of catKeys) {
    // For tiered groups, sum the MAX pts of each group (highest tier), plus standalone factors
    const factors = config.factors[k] || [];
    const groups = config.factors[k + "_groups"] || [];
    const groupedIds = new Set();
    let sum = 0;
    for (const grp of groups) {
      let maxInGroup = 0;
      for (const id of grp.ids) {
        groupedIds.add(id);
        const f = factors.find(x => x.id === id);
        if (f && f.pts > maxInGroup) maxInGroup = f.pts;
      }
      sum += maxInGroup;
    }
    for (const f of factors) {
      if (!groupedIds.has(f.id)) sum += f.pts;
    }
    factorSums[k] = sum;
    factorValid[k] = sum === config.categories[k].maxPts;
  }

  const allValid = catValid && catKeys.every(k => factorValid[k]);

  function updateCatMax(catKey, newMax) {
    const c = { ...config, categories: { ...config.categories, [catKey]: { ...config.categories[catKey], maxPts: newMax } } };
    setConfig(c);
    setSaved(false);
  }

  function updateFactorPts(catKey, factorId, newPts) {
    const c = { ...config, factors: { ...config.factors, [catKey]: config.factors[catKey].map(f => f.id === factorId ? { ...f, pts: newPts } : f) } };
    setConfig(c);
    setSaved(false);
  }

  function updateFactorThreshold(catKey, factorId, newThreshold) {
    const c = { ...config, factors: { ...config.factors, [catKey]: config.factors[catKey].map(f => f.id === factorId ? { ...f, threshold: newThreshold } : f) } };
    setConfig(c);
    setSaved(false);
  }

  function handleSave() {
    if (!allValid) return;
    saveRiskConfig(config);
    setSaved(true);
    if (onConfigChange) onConfigChange(config);
    setTimeout(() => setSaved(false), 3000);
  }

  function handleReset() {
    if (!window.confirm("Reset all risk settings to defaults?")) return;
    const def = resetRiskConfig();
    setConfig(def);
    setSaved(false);
    if (onConfigChange) onConfigChange(def);
  }

  const catColors = { revenue: T.red, position: T.cyan, compliance: T.amber, strategy: T.purple };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Broker Risk Score Settings</div>
          <div style={{ fontSize: 12, color: T.textMuted }}>Customize risk categories, factors, thresholds, and points</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={handleReset}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", borderRadius: 8,
              border: `1px solid ${T.border}`, background: T.bgCard, color: T.textDim, cursor: "pointer", fontSize: 12 }}>
            <RotateCcw size={14} /> Reset Defaults
          </button>
          <button onClick={handleSave} disabled={!allValid}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", borderRadius: 8,
              border: "none", background: allValid ? T.cyan : T.textMuted, color: "#000",
              cursor: allValid ? "pointer" : "not-allowed", fontSize: 12, fontWeight: 700, opacity: allValid ? 1 : 0.5 }}>
            <Save size={14} /> {saved ? "Saved!" : "Save Changes"}
          </button>
        </div>
      </div>

      {/* Category Weights */}
      <div style={{ background: T.bgCard, borderRadius: 12, border: `1px solid ${T.border}`, padding: 20, marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 600 }}>Category Weights</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {catValid ? (
              <><CheckCircle size={14} color={T.green} /><span style={{ fontSize: 12, color: T.green, fontWeight: 600 }}>Total: {catSum}/100</span></>
            ) : (
              <><AlertTriangle size={14} color={T.red} /><span style={{ fontSize: 12, color: T.red, fontWeight: 600 }}>Total: {catSum}/100 — must equal 100</span></>
            )}
          </div>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {catKeys.map(k => {
            const cat = config.categories[k];
            return (
              <div key={k} style={{ padding: 14, borderRadius: 10, background: T.bg, border: `1px solid ${catColors[k]}30` }}>
                <div style={{ fontSize: 10, color: catColors[k], textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, fontWeight: 700 }}>{cat.label}</div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="number" min={0} max={100} value={cat.maxPts}
                    onChange={e => updateCatMax(k, parseInt(e.target.value) || 0)}
                    style={{ width: 60, padding: "6px 8px", borderRadius: 6, border: `1px solid ${T.border}`, background: T.bgInput,
                      color: T.text, fontSize: 16, fontWeight: 700, textAlign: "center" }} />
                  <span style={{ fontSize: 12, color: T.textDim }}>pts</span>
                </div>
              </div>
            );
          })}
        </div>
        {/* Visual bar */}
        <div style={{ display: "flex", height: 8, borderRadius: 4, overflow: "hidden", marginTop: 14 }}>
          {catKeys.map(k => (
            <div key={k} style={{ width: config.categories[k].maxPts + "%", background: catColors[k], transition: "width 0.3s" }} />
          ))}
        </div>
      </div>

      {/* Risk Levels */}
      <div style={{ background: T.bgCard, borderRadius: 12, border: `1px solid ${T.border}`, padding: 20, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 14 }}>Risk Levels</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10 }}>
          {config.levels.map((lvl, i) => (
            <div key={i} style={{ padding: 12, borderRadius: 8, background: `${lvl.color}10`, border: `1px solid ${lvl.color}30`, textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: lvl.color, marginBottom: 4 }}>{lvl.label}</div>
              <div style={{ fontSize: 11, color: T.textDim }}>{lvl.min} — {lvl.max}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Factor Details per Category */}
      {catKeys.map(k => {
        const cat = config.categories[k];
        const factors = config.factors[k] || [];
        const groups = config.factors[k + "_groups"] || [];
        const isExpanded = expanded[k];

        return (
          <div key={k} style={{ background: T.bgCard, borderRadius: 12, border: `1px solid ${catColors[k]}30`, marginBottom: 14, overflow: "hidden" }}>
            {/* Category Header */}
            <div onClick={() => setExpanded({ ...expanded, [k]: !isExpanded })}
              style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px",
                cursor: "pointer", borderBottom: isExpanded ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {isExpanded ? <ChevronDown size={16} color={catColors[k]} /> : <ChevronRight size={16} color={catColors[k]} />}
                <span style={{ fontSize: 14, fontWeight: 700, color: catColors[k] }}>{cat.label}</span>
                <span style={{ fontSize: 11, color: T.textMuted }}>({factors.length} factors)</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {factorValid[k] ? (
                  <><CheckCircle size={14} color={T.green} /><span style={{ fontSize: 12, color: T.green, fontWeight: 600 }}>{factorSums[k]}/{cat.maxPts}</span></>
                ) : (
                  <><AlertTriangle size={14} color={T.red} /><span style={{ fontSize: 12, color: T.red, fontWeight: 600 }}>{factorSums[k]}/{cat.maxPts} — {factorSums[k] > cat.maxPts ? `over by ${factorSums[k] - cat.maxPts}` : `under by ${cat.maxPts - factorSums[k]}`}</span></>
                )}
              </div>
            </div>

            {isExpanded && (
              <div style={{ padding: 16 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ borderBottom: `1px solid ${T.border}` }}>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "5%" }}>Group</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "25%" }}>Factor</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "12%" }}>Type</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "18%" }}>Column</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "15%" }}>Threshold</th>
                      <th style={{ textAlign: "center", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "10%" }}>Points</th>
                      <th style={{ textAlign: "left", padding: "8px 10px", fontSize: 10, color: T.textMuted, width: "15%" }}>Tier Info</th>
                    </tr>
                  </thead>
                  <tbody>
                    {factors.map((f, fi) => {
                      // Find which group this factor belongs to
                      const grp = groups.find(g => g.ids.includes(f.id));
                      const grpIdx = grp ? grp.ids.indexOf(f.id) : -1;
                      const isFirstInGroup = grpIdx === 0;
                      const isInGroup = !!grp;

                      return (
                        <tr key={f.id} style={{ borderBottom: `1px solid ${T.border}15`,
                          background: isInGroup ? `${catColors[k]}03` : "transparent" }}>
                          <td style={{ padding: "6px 10px", fontSize: 11, color: T.textMuted }}>
                            {isFirstInGroup && <span style={{ fontSize: 9, background: `${catColors[k]}15`, color: catColors[k],
                              padding: "2px 6px", borderRadius: 4, fontWeight: 600 }}>TIER</span>}
                          </td>
                          <td style={{ padding: "6px 10px", fontSize: 12, color: isInGroup && !isFirstInGroup ? T.textDim : T.text,
                            paddingLeft: isInGroup && !isFirstInGroup ? 24 : 10 }}>
                            {f.label}
                          </td>
                          <td style={{ padding: "6px 10px" }}>
                            <span style={{ fontSize: 10, padding: "2px 6px", borderRadius: 4, fontWeight: 600,
                              background: f.type === "flag" ? `${T.amber}15` : f.type === "computed" ? `${T.purple}15` : `${T.cyan}15`,
                              color: f.type === "flag" ? T.amber : f.type === "computed" ? T.purple : T.cyan }}>
                              {f.type}
                            </span>
                          </td>
                          <td style={{ padding: "6px 10px", fontSize: 11, color: T.textDim, fontFamily: "monospace" }}>
                            {f.column || f.compute}
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            {f.type !== "flag" ? (
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                                <span style={{ fontSize: 10, color: T.textMuted }}>{f.op}</span>
                                <input type="number" value={f.threshold}
                                  onChange={e => updateFactorThreshold(k, f.id, parseFloat(e.target.value) || 0)}
                                  style={{ width: 70, padding: "3px 6px", borderRadius: 4, border: `1px solid ${T.border}`,
                                    background: T.bgInput, color: T.text, fontSize: 11, textAlign: "center" }} />
                              </div>
                            ) : (
                              <span style={{ fontSize: 10, color: T.textMuted }}>= {f.flagValue}</span>
                            )}
                          </td>
                          <td style={{ padding: "6px 10px", textAlign: "center" }}>
                            <input type="number" min={0} max={30} value={f.pts}
                              onChange={e => updateFactorPts(k, f.id, parseInt(e.target.value) || 0)}
                              style={{ width: 44, padding: "4px 6px", borderRadius: 6, border: `1px solid ${T.border}`,
                                background: T.bgInput, color: f.pts >= 6 ? T.red : f.pts >= 3 ? T.amber : T.text,
                                fontSize: 13, fontWeight: 700, textAlign: "center" }} />
                          </td>
                          <td style={{ padding: "6px 10px", fontSize: 10, color: T.textMuted }}>
                            {isInGroup ? `${grp.label} (highest wins)` : "standalone"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}

      {/* Validation Summary */}
      {!allValid && (
        <div style={{ background: T.redBg, borderRadius: 10, border: `1px solid ${T.red}30`, padding: 16, marginTop: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <AlertTriangle size={16} color={T.red} />
            <span style={{ fontSize: 13, fontWeight: 600, color: T.red }}>Cannot save — fix these issues:</span>
          </div>
          <div style={{ fontSize: 12, color: T.textDim, lineHeight: 2 }}>
            {!catValid && <div>• Category weights total is {catSum} — must equal 100</div>}
            {catKeys.filter(k => !factorValid[k]).map(k => (
              <div key={k}>• {config.categories[k].label}: factors sum is {factorSums[k]} — must equal {config.categories[k].maxPts}</div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
